import { useEffect, useState, useCallback, CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { KizGoodsGroup } from "../../../../@types/enumsGlobal";
import { PopoverClue } from "../../../../components/popoverClue/PopoverClue";
import SingleTagSelect, { SingleTagSelectOption } from "../../../../components/selects/tagSelect/singleTagSelect";

export const KIZ_CATEGORY = {
    medicines: 'medicines',
    gisMt: 'gisMt'
} as const;

export interface IKizCategorySelectProps {
    variant?: 'grid' | 'modal';
    className?: string;
    containerStyle?: CSSProperties;
    size?: 'small' | 'default' | 'large';
    rowHeight?: number
    kizData?: any[];
    kizOsuDtos?: any[];
    isLs?: boolean;
    currentItem?: any;
    idGlobal?: string;
    setKizData?: (data: any[]) => void;
    setKizOsuDtos?: (data: any[]) => void;
    setIsLs?: (value: boolean) => void;
    onDataChange?: (value: any) => void;
    onSwitchType?: (idGlobal: string, newType: string, oldType: string) => void;
    disabled?: boolean;
    showPopover?: boolean;
    popoverContent?: string;
}

export const KizCategorySelect: React.FC<IKizCategorySelectProps> = ({
    variant = 'modal',
    kizData = [],
    kizOsuDtos = [],
    currentItem,
    idGlobal = '',
    setKizData,
    setKizOsuDtos,
    setIsLs,
    onDataChange,
    onSwitchType,
    disabled = false,
    size = 'default',
    rowHeight = 42,
    showPopover = false,
    className,
    containerStyle,
    popoverContent
}) => {
    const { t } = useTranslation();
    const baseT = (value: string) => t('kizScanModal.' + value);
    
    const moveToCategory = {
        medicines: baseT('moveToMedicines'),
        gisMt: baseT('moveToGisMt')
    } as const;

    const kizCategoryLabels = {
        medicines: baseT('medicines'),
        gisMt: baseT('gisMt')
    };

    const kizCategoryOptions: SingleTagSelectOption[] = [
        {
            value: KIZ_CATEGORY.medicines,
            label: kizCategoryLabels.medicines,
            dropdownLabel: moveToCategory.medicines,
            color: '#009933'
        },
        {
            value: KIZ_CATEGORY.gisMt,
            label: kizCategoryLabels.gisMt,
            dropdownLabel: moveToCategory.gisMt,
            color: '#039bb3'
        },
    ];

    const getCurrentValue = useCallback(() => {
        if (variant === 'grid' && currentItem) {
            const hasKizData = (currentItem.kizs && currentItem.kizs.length > 0)
            const hasKizOsuData = (currentItem.kizOsuDtos && currentItem.kizOsuDtos.length > 0);
            
            if (hasKizData && !hasKizOsuData) {
                return KIZ_CATEGORY.medicines;
            } else if (!hasKizData && hasKizOsuData) {
                return KIZ_CATEGORY.gisMt;
            }
            return KIZ_CATEGORY.medicines;
        } else {
            const hasKizData = kizData && kizData.length > 0;
            const hasKizOsuData = kizOsuDtos && kizOsuDtos.length > 0;
            
            if (hasKizData && !hasKizOsuData) {
                return KIZ_CATEGORY.medicines;
            } else if (!hasKizData && hasKizOsuData) {
                return KIZ_CATEGORY.gisMt;
            }
            return KIZ_CATEGORY.medicines;
        }
    }, [variant, currentItem, kizData, kizOsuDtos]);

    const [selectedValue, setSelectedValue] = useState<string>(getCurrentValue());

    useEffect(() => {
        const newValue = getCurrentValue();
        if (newValue !== selectedValue) {
            setSelectedValue(newValue);
        }
    }, [getCurrentValue, selectedValue]);

    

    const handleChange = (newValue: string | number) => {
        const oldValue = selectedValue;
        const newValueStr = newValue as string;
        
        setSelectedValue(newValueStr);
        
        if (variant === 'modal') {
            if (oldValue === KIZ_CATEGORY.medicines && newValueStr === KIZ_CATEGORY.gisMt) {
                const updatedKizOsuDtos = (kizData || []).map(kiz => ({
                    ...kiz,
                    goodsGroup: KizGoodsGroup.WTRU
                }));
                setKizOsuDtos?.([...updatedKizOsuDtos, ...(kizOsuDtos || [])]);
                setKizData?.([]);
                setIsLs?.(false);
            } else if (oldValue === KIZ_CATEGORY.gisMt && newValueStr === KIZ_CATEGORY.medicines) {
                const updatedKizData = (kizOsuDtos || []).map(kiz => ({
                    ...kiz,
                    goodsGroup: KizGoodsGroup.MDLP
                }));
                setKizData?.([...updatedKizData, ...(kizData || [])]);
                setKizOsuDtos?.([]);
                setIsLs?.(true);
            }
        }
        
        if (variant === 'grid') {
            onDataChange?.(newValueStr);
            onSwitchType?.(idGlobal, newValueStr, oldValue);
        }
    };

    const selectComponent = (
        <SingleTagSelect
            options={kizCategoryOptions}
            value={selectedValue}
            onChange={handleChange}
            size={size}
            allowClear={false}
            disabled={disabled}
            className={`${className || ''}`}
            containerStyle={containerStyle}
            tagStyle={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 15,
                marginRight: 0, 
                width: '100%',
                height: rowHeight - 8,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
            }}
            showCheck
        />
    );

    if (showPopover) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 5 }}>
                {selectComponent}
                <PopoverClue clues={[popoverContent || baseT('clueMoveTypeKIZ')]} />
            </div>
        );
    }

    return selectComponent      
};

export default KizCategorySelect;